// src/utils/blogUtils.js

/**
 * Parse blog content safely from API response
 */
export const parseBlogContent = (content) => {
    if (!content) return '';
    return content
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');
};

/**
 * Calculate reading time from word count
 */
export const calculateReadingTime = (wordCount, wordsPerMinute = 200) => {
    return Math.ceil(wordCount / wordsPerMinute);
};

/**
 * Generate share URLs for social platforms
 */
export const generateShareUrls = (blog, currentUrl) => {
    const encodedUrl = encodeURIComponent(currentUrl);
    const encodedTitle = encodeURIComponent(blog.title);
    const encodedDescription = encodeURIComponent(blog.excerpt || blog.metaDescription);

    return {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`,
        pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodeURIComponent(blog.coverImage?.url || '')}&description=${encodedTitle}`,
        email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0ARead more: ${encodedUrl}`,
        whatsapp: `https://wa.me/?text=${encodedTitle}%0A%0A${encodedUrl}`,
    };
};

/**
 * Get category color based on category name
 */
export const getCategoryColor = (category) => {
    const colors = {
        skincare: '#4CAF50',
        wellness: '#2196F3',
        lifestyle: '#9C27B0',
        science: '#FF9800',
        sustainability: '#00BCD4',
        'makeup': '#E91E63',
        'haircare': '#8BC34A',
        default: '#666666',
    };
    return colors[category?.toLowerCase()] || colors.default;
};

/**
 * Filter and sort blogs based on criteria
 */
export const filterAndSortBlogs = (blogs, filters = {}) => {
    let filteredBlogs = [...blogs];

    // Filter by category
    if (filters.category && filters.category !== 'all') {
        filteredBlogs = filteredBlogs.filter(blog => blog.category === filters.category);
    }

    // Filter by search query
    if (filters.search) {
        const query = filters.search.toLowerCase();
        filteredBlogs = filteredBlogs.filter(blog =>
            blog.title.toLowerCase().includes(query) ||
            blog.excerpt?.toLowerCase().includes(query) ||
            blog.tags?.some(tag => tag.toLowerCase().includes(query)) ||
            blog.category?.toLowerCase().includes(query)
        );
    }

    // Filter by status
    if (filters.status) {
        filteredBlogs = filteredBlogs.filter(blog => blog.status === filters.status);
    }

    // Filter by featured
    if (filters.featured !== undefined) {
        filteredBlogs = filteredBlogs.filter(blog => blog.featured === filters.featured);
    }

    // Sort blogs
    if (filters.sort) {
        const sortOrder = filters.sort.startsWith('-') ? 'desc' : 'asc';
        const sortField = filters.sort.replace(/^-/, '');

        filteredBlogs.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            // Handle date sorting
            if (sortField.includes('date') || sortField.includes('Date') || sortField === 'createdAt' || sortField === 'publishedAt') {
                aValue = new Date(aValue || a.createdAt || 0);
                bValue = new Date(bValue || b.createdAt || 0);
            }

            // Handle numeric sorting
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // Default string sorting
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortOrder === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            // Date sorting
            if (aValue instanceof Date && bValue instanceof Date) {
                return sortOrder === 'asc'
                    ? aValue.getTime() - bValue.getTime()
                    : bValue.getTime() - aValue.getTime();
            }

            return 0;
        });
    }

    return filteredBlogs;
};

/**
 * Format date for blog display
 */
export const formatBlogDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Validate blog form data
 */
export const validateBlogForm = (formData) => {
    const errors = {};

    if (!formData.title?.trim()) errors.title = 'Title is required';
    if (!formData.slug?.trim()) errors.slug = 'Slug is required';
    if (!formData.category?.trim()) errors.category = 'Category is required';
    if (!formData.excerpt?.trim()) errors.excerpt = 'Excerpt is required';
    if (!formData.content?.trim()) errors.content = 'Content is required';
    if (!formData.metaTitle?.trim()) errors.metaTitle = 'Meta title is required';
    if (!formData.metaDescription?.trim()) errors.metaDescription = 'Meta description is required';
    if (!formData.focusKeyword?.trim()) errors.focusKeyword = 'Focus keyword is required';
    if (!formData.tags?.length) errors.tags = 'At least one tag is required';
    if (!formData.coverImage?.url) errors.coverImage = 'Cover image is required';
    if (!formData.coverImage?.alt) errors.coverImageAlt = 'Cover image alt text is required';

    return errors;
};